import { Graphics, Sprite, Texture, Application } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { RoomDefinition, RoomCategory } from '@gameplay/management/rooms/RoomTypes';
import { RoomLegend } from '@ui/RoomLegend';

export class FloorColorSystem {
  private readonly app: Application;
  private readonly gridSystem: GridSystem;
  private readonly roomManager: RoomManager;
  private floorTextures: Map<string, Texture> = new Map();
  private isEnabled: boolean = true; // Always enabled by default

  constructor(app: Application, gridSystem: GridSystem, roomManager: RoomManager) {
    this.app = app;
    this.gridSystem = gridSystem;
    this.roomManager = roomManager;
    
    // Create textures for all room categories
    this.generateFloorTextures();
    
    // Enable immediately and apply to all existing floors
    this.enable();
  }

  private generateFloorTextures(): void {
    // Create unassigned floor texture (default)
    this.floorTextures.set('unassigned', this.createFloorTexture(0xe8d8a0));

    // Create colored textures for each room category
    const categories: RoomCategory[] = ['bedroom', 'bathroom', 'dining', 'medical', 'recreation', 'utility', 'unspecified'];
    
    categories.forEach(category => {
      const baseColor = RoomLegend.getRoomColor(category);
      const floorColor = this.blendColors(0xe8d8a0, baseColor, 0.3); // 30% room color, 70% original
      this.floorTextures.set(category, this.createFloorTexture(floorColor));
    });
  }

  private createFloorTexture(fillColor: number): Texture {
    const graphic = new Graphics()
      .lineStyle(2, 0xb7a57a, 0.9)
      .beginFill(fillColor, 1)
      .drawPolygon([
        0,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        0,
        GridSystem.TILE_WIDTH,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        GridSystem.TILE_HEIGHT
      ])
      .endFill();

    const texture = this.app.renderer.generateTexture(graphic);
    graphic.destroy();
    return texture;
  }

  private blendColors(baseColor: number, overlayColor: number, alpha: number): number {
    const base = {
      r: (baseColor >> 16) & 0xff,
      g: (baseColor >> 8) & 0xff,
      b: baseColor & 0xff
    };

    const overlay = {
      r: (overlayColor >> 16) & 0xff,
      g: (overlayColor >> 8) & 0xff,
      b: overlayColor & 0xff
    };

    const blended = {
      r: Math.round(base.r * (1 - alpha) + overlay.r * alpha),
      g: Math.round(base.g * (1 - alpha) + overlay.g * alpha),
      b: Math.round(base.b * (1 - alpha) + overlay.b * alpha)
    };

    return (blended.r << 16) | (blended.g << 8) | blended.b;
  }

  public enable(): void {
    if (this.isEnabled) return;
    
    this.isEnabled = true;
    
    // Update all existing room floors
    const rooms = this.roomManager.getRooms();
    rooms.forEach(room => this.updateRoomFloorColors(room));
    
    // Update unassigned floors
    this.updateUnassignedFloors();
  }

  public disable(): void {
    if (!this.isEnabled) return;
    
    this.isEnabled = false;
    
    // Revert all floor tiles to default color
    const allObjects = this.gridSystem.getAllObjects();
    const floorObjects = allObjects.filter(obj => obj.type === 'floor');
    
    floorObjects.forEach(floor => {
      if (floor.sprite && floor.sprite instanceof Sprite) {
        floor.sprite.texture = this.floorTextures.get('unassigned')!;
      }
    });
  }

  // Room floor colors are always enabled - toggle functionality removed
  public toggle(): void {
    // No longer toggleable - always enabled for better UX
  }

  public updateRoomFloorColors(room: RoomDefinition): void {
    if (!this.isEnabled) return;

    const texture = this.floorTextures.get(room.category) || this.floorTextures.get('unassigned')!;
    
    room.tiles.forEach(tile => {
      const floorObject = this.gridSystem.getObjectByType(tile.gridX, tile.gridY, tile.floor, 'floor');
      if (floorObject && floorObject.sprite && floorObject.sprite instanceof Sprite) {
        floorObject.sprite.texture = texture;
      }
    });
  }

  private updateUnassignedFloors(): void {
    if (!this.isEnabled) return;

    const rooms = this.roomManager.getRooms();
    const assignedTiles = new Set<string>();
    
    // Mark all assigned tiles
    rooms.forEach(room => {
      room.tiles.forEach(tile => {
        assignedTiles.add(`${tile.gridX}_${tile.gridY}_${tile.floor}`);
      });
    });

    // Find and update unassigned floor tiles
    const allObjects = this.gridSystem.getAllObjects();
    const floorObjects = allObjects.filter(obj => obj.type === 'floor');
    const unassignedTexture = this.floorTextures.get('unassigned')!;
    
    floorObjects.forEach(floor => {
      const tileKey = `${floor.gridX}_${floor.gridY}_${floor.floor}`;
      if (!assignedTiles.has(tileKey) && floor.sprite && floor.sprite instanceof Sprite) {
        floor.sprite.texture = unassignedTexture;
      }
    });
  }

  public updateFloorTile(gridX: number, gridY: number, floor: number): void {
    if (!this.isEnabled) return;

    const room = this.roomManager.getRoomAt(gridX, gridY, floor);
    const floorObject = this.gridSystem.getObjectByType(gridX, gridY, floor, 'floor');
    
    if (floorObject && floorObject.sprite && floorObject.sprite instanceof Sprite) {
      let texture: Texture;
      
      if (room) {
        texture = this.floorTextures.get(room.category) || this.floorTextures.get('unassigned')!;
      } else {
        texture = this.floorTextures.get('unassigned')!;
      }
      
      floorObject.sprite.texture = texture;
    }
  }

  /**
   * Refresh all floor tile textures to match current room assignments.
   * Useful after bulk operations (like drag categorization) to ensure consistency.
   */
  public refreshAll(): void {
    if (!this.isEnabled) return;
    // Update every room's tiles and then fix any unassigned tiles
    this.roomManager.getRooms().forEach(room => this.updateRoomFloorColors(room));
    this.updateUnassignedFloors();
  }

  public getFloorTexture(category: RoomCategory | null): Texture {
    if (!category) {
      return this.floorTextures.get('unassigned')!;
    }
    return this.floorTextures.get(category) || this.floorTextures.get('unassigned')!;
  }

  public get enabled(): boolean {
    return this.isEnabled;
  }
}